import React from "react";
import InputWrapper from "../../inputWrapper";
import { FirstLastName } from "../../../styles/common";
import Button from "../../button";
import PasswordChecker from "../passwordChecker";
import { whiteEyeIcon, whiteCloseEyeIcon } from "../../../images";
import styles from "./signUp.module.css";
import { getNames } from "country-list";

const SignUpFormBase = ({
  onSubmit,
  register,
  control,
  handleSubmit,
  errors,
  passwordValue,
  isDirty,
  isValid,
  showPassword,
  handleShowPassword,
  formType = "user",
  isLoading,
}) => {
  const countries = getNames();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles["login_form"]}>
      {/* First and Last Name */}
      <FirstLastName>
        <InputWrapper
          title="First Name"
          fieldName="firstname"
          requiredMessage="Enter valid firstname"
          register={register}
          errors={errors}
        />
        <InputWrapper
          title="Last Name"
          fieldName="lastname"
          requiredMessage="Enter valid lastname"
          register={register}
          errors={errors}
        />
      </FirstLastName>

      {/* Username (user only) or Country (mentor only) */}
      <FirstLastName>
        {formType === "user" ? (
          <>
            <InputWrapper
              title="Username"
              type="text"
              fieldName="username"
              requiredMessage="Username is required"
              register={register}
              errors={errors}
            />
            <div /> {/* Placeholder to keep layout symmetrical */}
          </>
        ) : (
          <>
            {/* Country select (50% width like first/last name) */}
            <div className="w-full relative mb-4 ">
              <label className="block font-normal text-[12px] leading-[20px] tracking-normal text-[#FCFCF5]">
                Country
              </label>
              <select
                {...register("country", {
                  required: "Please select a country",
                })}
                className={`w-full h-[51px] bg-inherit rounded p-4 pr-10 text-white text-[12px] appearance-none border-2 focus:outline-none focus:ring-0
                    ${
                      errors.country
                        ? "border-red-500 focus:border-red-500"
                        : "border-[#D8D8D8] focus:border-[#F8D210]"
                    }`}
                defaultValue=""
              >
                <option className="text-xs" value="" disabled>
                  Select
                </option>
                {countries.map((country, idx) => (
                  <option
                    key={idx}
                    value={country}
                    className="text-white bg-black text-[12px]"
                  >
                    {country}
                  </option>
                ))}
              </select>

              {/* Dropdown icon */}
              <div className="pointer-events-none mt-[-22px] absolute right-4 transform -translate-y-1/2">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {errors.country && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.country.message}
                </p>
              )}
            </div>

            {/* Location input (50% width) */}
            <InputWrapper
              title="Location"
              type="text"
              fieldName="location"
              requiredMessage="Please enter your location"
              register={register}
              errors={errors}
            />
          </>
        )}
      </FirstLastName>

      {/* Email & Phone (mentor) or just Email (user) */}
      {formType === "mentor" ? (
        <>
          <InputWrapper
            title="Email"
            type="email"
            placeholder="Example@example.com"
            fieldName="email"
            requiredMessage="Email is required"
            patternValue={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
            patternMessage="Please enter a valid email address. Example: name@gmail.com"
            compoundValidation={true}
            register={register}
            errors={errors}
          />

          <InputWrapper
            inputTag="Phone Input"
            title="Phone Number"
            fieldName="phone_number"
            requiredMessage="Phone number is required"
            compoundValidation={true}
            control={control}
            register={register}
            errors={errors}
          />
        </>
      ) : (
        <InputWrapper
          title="Email"
          type="email"
          placeholder="Example@example.com"
          fieldName="email"
          requiredMessage="Email is required"
          patternValue={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
          patternMessage="Please enter a valid email address. Example: name@gmail.com"
          compoundValidation={true}
          register={register}
          errors={errors}
        />
      )}

      {/* Password Fields */}
      <div className="mb-3">
        <InputWrapper
          inputTag="Password"
          title="Create Password"
          type={showPassword ? "text" : "password"}
          fieldName="password"
          requiredMessage="Password is Required"
          compoundValidation={true}
          register={register}
          errors={errors}
          icon={showPassword || !isDirty ? whiteEyeIcon : whiteCloseEyeIcon}
          onImageClick={handleShowPassword}
          patternValue={
            /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/
          }
          patternMessage="Low Password Strength"
          className={"!text-[14px]"}
        />
        <PasswordChecker passwordValue={passwordValue} />
      </div>

      <InputWrapper
        inputTag="Password"
        title="Confirm Password"
        type={showPassword ? "text" : "password"}
        fieldName="confirmpassword"
        requiredMessage="Password is Required"
        compoundValidation={true}
        {...register("confirmpassword", {
          validate: (value) =>
            value === passwordValue || "The new password fields do not match.",
        })}
        register={register}
        errors={errors}
        icon={showPassword || !isDirty ? whiteEyeIcon : whiteCloseEyeIcon}
        onImageClick={handleShowPassword}
        className={"!text-[14px]"}
      />

      {/* Mentor-only fields removed: Specialization and Years of Experience */}

      {/* Terms & Submit */}
      <label className="text-[#D8D8D8] font-normal text-xs leading-5 mb-[-.5rem]">
        By creating an account, you accept our{" "}
        <button 
          type="button"
          onClick={() => {
            window.open(window.location.origin + '/terms-and-conditions', '_blank');
          }}
          className="underline hover:text-[#F8D210] cursor-pointer bg-none border-none p-0 font-inherit text-inherit"
        >
          Terms
        </button>
        .
      </label>

      <Button
        size="full"
        variant={isValid && !isLoading ? "secondary" : "disabled"}
        type="submit"
        className="mt-4 bg-[#D8D8D8]"
        disabled={!isValid || isLoading}
      >
        {isLoading ? "Creating account..." : "Create account"}
      </Button>
    </form>
  );
};

export default SignUpFormBase;
